import React, { useState, useRef, useEffect } from 'react';
import { Z_INDEX, DEFAULT_LABEL_COUNT_CONFIG } from './const';
import {
  calculatePositionPercentage,
  parseStepOptionValue,
  calculateOrderOfMagnitude,
  calculateTargetLabelCount,
  getMinDistanceThreshold,
  isLabelTooClose,
  roundToIncrement,
  calculateRoundingIncrement,
  formatDisplayValue,
  type LabelCountConfig,
} from './utils';

export type LabelData = {
  value: number;
  position: number;
  isStepOption: boolean;
  label: string;
};

type SliderProps = {
  value?: number;
  min: number;
  max: number;
  step?: number;
  stepOptions: string[];
  onChange?: (value: number) => void;
  labelCountConfig?: LabelCountConfig;
};

export const Slider: React.FC<SliderProps> = ({
  value = 0,
  min,
  max,
  step = 1,
  stepOptions = [],
  onChange,
  labelCountConfig = DEFAULT_LABEL_COUNT_CONFIG,
}) => {
  const [localValue, setLocalValue] = useState(value);
  const [isDragging, setIsDragging] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const sliderRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = Number(e.target.value);
    setLocalValue(newValue);
    onChange?.(newValue);
  };

  const handleMouseDown = () => {
    setIsDragging(true);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseEnter = () => {
    setIsHovering(true);
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
    setIsDragging(false);
  };

  const handleTouchStart = () => {
    setIsDragging(true);
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  const getTooltipPosition = (): number => {
    return calculatePositionPercentage(localValue, min, max);
  };

  const createStepOptionLabels = (): LabelData[] => {
    return stepOptions.map((label, index) => {
      const numericValue = parseStepOptionValue(
        label,
        index,
        stepOptions.length,
        min,
        max
      );
      const position = calculatePositionPercentage(numericValue, min, max);

      return {
        value: numericValue,
        position,
        isStepOption: true,
        label,
      };
    });
  };

  const generateIntermediateLabels = (
    existingLabels: LabelData[],
    range: number,
    roundingIncrement: number
  ): LabelData[] => {
    const magnitude = Math.pow(10, calculateOrderOfMagnitude(range));
    const targetTotalLabels = calculateTargetLabelCount(
      range,
      magnitude,
      labelCountConfig
    );
    const evenlySpacedCount = targetTotalLabels - stepOptions.length;

    if (evenlySpacedCount <= 0) {
      return [];
    }

    const minDistanceThreshold = getMinDistanceThreshold(range);
    const newLabels: LabelData[] = [];

    for (let i = 0; i <= evenlySpacedCount; i++) {
      const evenlySpacedValue = min + (i / evenlySpacedCount) * range;
      const position = calculatePositionPercentage(evenlySpacedValue, min, max);

      if (!isLabelTooClose(position, existingLabels, minDistanceThreshold)) {
        const formattedValue = roundToIncrement(
          evenlySpacedValue,
          roundingIncrement
        );

        newLabels.push({
          value: evenlySpacedValue,
          position,
          isStepOption: false,
          label: String(formattedValue),
        });
      }
    }

    return newLabels;
  };

  const generateEvenlySpacedLabels = (): LabelData[] => {
    const stepOptionLabels = createStepOptionLabels();
    const range = max - min;
    const magnitude = Math.pow(10, calculateOrderOfMagnitude(range));
    const roundingIncrement = calculateRoundingIncrement(magnitude);

    const intermediateLabels = generateIntermediateLabels(
      stepOptionLabels,
      range,
      roundingIncrement
    );

    const allLabels = [...stepOptionLabels, ...intermediateLabels];
    return allLabels.sort((a, b) => a.position - b.position);
  };

  const tooltipPosition = getTooltipPosition();
  const displayValue = formatDisplayValue(localValue);
  const showTooltip = isDragging || isHovering;
  const allLabels = generateEvenlySpacedLabels();

  return (
    <div
      ref={containerRef}
      className="w-full flex flex-col items-center border border-gray-300 rounded-full pt-10 pb-8 px-6 bg-gradient-to-b from-gray-50 to-gray-100 shadow-[0_8px_16px_rgba(0,0,0,0.1),0_4px_8px_rgba(0,0,0,0.06),inset_0_1px_0_rgba(255,255,255,0.8)] relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="relative w-full">
        <input
          ref={sliderRef}
          type="range"
          min={min}
          max={max}
          step={step}
          value={localValue}
          onChange={handleChange}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          className="w-full appearance-none h-2 bg-gray-300 rounded-lg outline-none transition-all relative cursor-pointer"
          style={{ zIndex: Z_INDEX.SLIDER }}
        />

        {showTooltip && (
          <div
            className="absolute bottom-full mb-2 -translate-x-1/2 pointer-events-none transition-opacity duration-150"
            style={{
              left: `${tooltipPosition}%`,
              opacity: showTooltip ? 1 : 0,
              zIndex: Z_INDEX.TOOLTIP,
            }}
          >
            <div className="bg-gray-900 text-white text-xs font-medium px-2 py-1 rounded shadow-lg whitespace-nowrap">
              {displayValue}
              <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
            </div>
          </div>
        )}
      </div>

      <div className="relative w-full flex justify-between text-gray-500 text-tiny mt-2">
        {allLabels.map((labelData, index) => (
          <span
            key={`${labelData.position}-${index}`}
            className="absolute -translate-x-1/2"
            style={{ left: `${labelData.position}%` }}
          >
            {labelData.label}
          </span>
        ))}
      </div>
    </div>
  );
};
